import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { createRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { EvidenceRecordView, evidenceSearchSchema, recordQuery, type EvidenceSearch } from "@amber/evidence/ui";
import { isApiError } from "@amber/ui";
import { NoAccessPage } from "../pages/NoAccess";
import { NotFoundPage } from "../pages/NotFound";
import { AppFrame } from "../shell/AppFrame";
import { usePageTitle } from "../shell/title";
import { rootRoute } from "./root";

export const evidenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/items/$itemRef",
  validateSearch: evidenceSearchSchema,
  component: function EvidenceRoute() {
    const { itemRef } = evidenceRoute.useParams();
    const search = evidenceRoute.useSearch();
    const navigate = useNavigate();
    const [itemId = "", versionText] = decodeURIComponent(itemRef).split("@");
    const version = versionText === undefined ? undefined : Number(versionText) || undefined;
    usePageTitle("Evidence record");
    const href = useRouterState({ select: (st) => st.location.href });
    const probe = useQuery(recordQuery(itemId, version, search.communication));
    const probeStatus = isApiError(probe.error) ? probe.error.status : 0;
    const onSearch = useCallback(
      (next: EvidenceSearch, nextVersion?: number | null) => {
        const v = nextVersion === undefined ? version : (nextVersion ?? undefined);
        void navigate({
          to: "/items/$itemRef",
          params: { itemRef: v ? `${itemId}@${String(v)}` : itemId },
          search: next,
          replace: nextVersion === undefined,
        });
      },
      [navigate, itemId, version],
    );
    if (probeStatus === 403) return <NoAccessPage what={href} />;
    if (probeStatus === 404 || probeStatus === 400) return <NotFoundPage lookedUp={href} />;
    return (
      <AppFrame view="Evidence record">
        {(session) => (
          <EvidenceRecordView
            key={`${itemId}@${String(version)}`}
            itemId={itemId}
            version={version}
            search={search}
            role={session.user.role}
            onSearch={onSearch}
          />
        )}
      </AppFrame>
    );
  },
});
